import React, { useState } from 'react';
import { apiPost, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface Team {
  team_id: string;
  name: string;
  aws_account_assignment?: string;
}

interface AssignmentResult {
  team_id: string;
  team_name: string;
  aws_account: string;
  account_number: string;
  action: 'assigned' | 'kept';
}

interface AssignmentResponse {
  event_id: string;
  event_name: string;
  total_teams: number;
  assignments_made: number;
  assignments_kept: number;
  assignments: AssignmentResult[];
}

interface AwsAccountAssignmentProps {
  eventId: string;
  teams: Team[];
  onAssignmentComplete: () => void;
}

const AwsAccountAssignment: React.FC<AwsAccountAssignmentProps> = ({
  eventId,
  teams,
  onAssignmentComplete
}) => {
  const [assigning, setAssigning] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [assignmentResults, setAssignmentResults] = useState<AssignmentResponse | null>(null);
  const [error, setError] = useState<string>('');
  const { token } = useAuth();

  const unassignedTeamsCount = teams.filter(team => !team.aws_account_assignment).length;
  const assignedTeamsCount = teams.filter(team => team.aws_account_assignment).length;

  const handleAssignAccounts = async () => {
    if (!token) return;
    
    setAssigning(true);
    setError('');
    
    try {
      const response = await apiPost<AssignmentResponse>(
        `/teams/${eventId}/assign-aws-accounts`,
        {},
        token
      );
      
      setAssignmentResults(response);
      setShowResults(true);
      onAssignmentComplete();
      
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to assign AWS accounts');
      }
    } finally {
      setAssigning(false);
    }
  };

  const getAwsAccountDisplay = (team: Team) => {
    if (!team.aws_account_assignment) {
      return (
        <span style={{ 
          color: '#ffc107', 
          fontWeight: 'bold',
          fontSize: '12px'
        }}>
          🔄 Pending
        </span>
      );
    }
    
    return (
      <span style={{ 
        color: '#28a745', 
        fontWeight: 'bold',
        fontSize: '12px'
      }}>
        ☁️ {team.aws_account_assignment}
      </span>
    );
  };

  if (showResults && assignmentResults) {
    return (
      <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>☁️ AWS Account Assignments</h3>
          <button 
            onClick={() => setShowResults(false)}
            className="btn btn-secondary"
            style={{ fontSize: '14px', padding: '8px 16px' }}
          >
            Hide Results
          </button>
        </div>
        
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#d4edda', 
          borderRadius: '8px', 
          border: '1px solid #c3e6cb',
          marginBottom: '20px'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#155724' }}>
            ✅ Assignment Complete!
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
            <div>
              <strong>Total Teams:</strong> {assignmentResults.total_teams}
            </div>
            <div>
              <strong>New Assignments:</strong> {assignmentResults.assignments_made}
            </div>
            <div>
              <strong>Existing Kept:</strong> {assignmentResults.assignments_kept}
            </div>
          </div>
        </div>

        <div className="grid">
          {assignmentResults.assignments.map((assignment) => (
            <div key={assignment.team_id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, flex: 1 }}>{assignment.team_name}</h4>
                <span 
                  style={{
                    background: assignment.action === 'assigned' ? '#28a745' : '#6c757d',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 'bold'
                  }}
                >
                  {assignment.action === 'assigned' ? '🆕 New' : '📌 Kept'}
                </span>
              </div>
              <div style={{ fontSize: '14px' }}>
                <div style={{ marginBottom: '5px' }}>
                  <strong>AWS Account:</strong> {assignment.aws_account}
                </div>
                <div style={{ color: '#666', fontSize: '12px' }}>
                  Account Number: {assignment.account_number}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3>☁️ AWS Account Assignments</h3>
        {teams.length > 0 && (
          <button 
            onClick={handleAssignAccounts}
            disabled={assigning}
            className="btn btn-primary"
            style={{ fontSize: '14px', padding: '8px 16px' }}
          >
            {assigning ? '⏳ Assigning...' : '🔄 Assign AWS Accounts'}
          </button>
        )}
      </div>

      {error && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#f8d7da', 
          borderRadius: '8px', 
          border: '1px solid #f5c6cb',
          marginBottom: '20px',
          color: '#721c24'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {teams.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h4>No teams to assign</h4>
          <p style={{ color: '#666' }}>
            Create teams first, then assign them to AWS accounts.
          </p>
        </div>
      ) : (
        <div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '20px',
            marginBottom: '20px'
          }}>
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#e7f3ff', 
              borderRadius: '8px', 
              border: '1px solid #b3d7ff' 
            }}>
              <h4 style={{ margin: '0 0 5px 0' }}>📊 Assignment Status</h4>
              <div>✅ Assigned: {assignedTeamsCount}</div>
              <div>🔄 Pending: {unassignedTeamsCount}</div>
            </div>
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#fff3cd', 
              borderRadius: '8px', 
              border: '1px solid #ffeaa7' 
            }}>
              <h4 style={{ margin: '0 0 5px 0' }}>ℹ️ About Assignments</h4>
              <div style={{ fontSize: '12px', color: '#856404' }}>
                Teams are evenly distributed across 5 AWS hackathon accounts.
                Existing assignments are preserved.
              </div>
            </div>
          </div>

          <div className="grid">
            {teams.map((team) => (
              <div key={team.team_id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <h4 style={{ margin: 0, flex: 1 }}>{team.name}</h4>
                  {getAwsAccountDisplay(team)}
                </div>
                {team.aws_account_assignment && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    AWS Account: {team.aws_account_assignment}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AwsAccountAssignment;
